import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import type {
  BudgetRequestDecisionIn,
  BudgetRequestListOut,
  DriftListOut,
  GuardrailRequestDecisionIn,
  GuardrailRequestListOut,
  Identity,
  JobStatusListOut,
  TeamConfigListOut,
  TeamSummary,
  UnmanagedKeyListOut,
} from '../types';
import { BudgetRequestList } from './BudgetRequestList';
import { DriftList } from './DriftList';
import { GuardrailRequestList } from './GuardrailRequestList';
import { JobStatusView } from './JobStatusView';
import { TeamConfigList } from './TeamConfigList';
import { UnmanagedKeyList } from './UnmanagedKeyList';

interface AdminPanelProps {
  teams: TeamSummary[];
  identity: Identity | null;
  /** Reload the parent's key list: a claimed key leaves this panel and becomes a
   * managed row over there, so refreshing only this panel would leave the main list
   * still showing it as "owner unknown". */
  onClaimed?: () => void;
}

interface AdminData {
  unmanaged: UnmanagedKeyListOut | null;
  drift: DriftListOut | null;
  jobs: JobStatusListOut | null;
  budgetRequests: BudgetRequestListOut | null;
  guardrailRequests: GuardrailRequestListOut | null;
  teamConfig: TeamConfigListOut | null;
}

/**
 * Admin surface: unmanaged key adoption, drift monitoring, job health, and budget requests.
 * Admin endpoint failures are never fatal to the main key view — they return 404
 * for non-admins by design and must degrade gracefully.
 */
export function AdminPanel({ teams, identity, onClaimed }: AdminPanelProps) {
  const [data, setData] = useState<AdminData>({
    unmanaged: null,
    drift: null,
    jobs: null,
    budgetRequests: null,
    guardrailRequests: null,
    teamConfig: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    'unmanaged' | 'drift' | 'jobs' | 'budget' | 'guardrail' | 'teams'
  >('unmanaged');
  // Generation counter, same pattern as KeyView: superseded loads cannot write state.
  const generation = useRef(0);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    const current = () => gen === generation.current;
    setLoading(true);
    setError(null);
    try {
      const [unmanaged, drift, jobs, budgetRequests, guardrailRequests, teamConfig] =
        await Promise.all([
          api.unmanagedKeys('pending').catch(() => null),
          api.drift().catch(() => null),
          api.jobStatus().catch(() => null),
          api.budgetRequests().catch(() => null),
          api.guardrailRequests().catch(() => null),
          api.teamConfig().catch(() => null),
        ]);
      if (!current()) return;
      setData({ unmanaged, drift, jobs, budgetRequests, guardrailRequests, teamConfig });
    } catch (err) {
      if (!current()) return;
      // Only set error if we get something other than a 404 (which is expected for non-admins).
      if (err instanceof ApiError && err.status === 404) {
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  const handleApprove = async (id: string, decisionData: BudgetRequestDecisionIn) => {
    await api.approveBudgetRequest(id, decisionData);
    await load();
  };

  const handleReject = async (id: string, decisionData: BudgetRequestDecisionIn) => {
    await api.rejectBudgetRequest(id, decisionData);
    await load();
  };

  const handleApproveGuardrail = async (id: string, decisionData: GuardrailRequestDecisionIn) => {
    await api.approveGuardrailRequest(id, decisionData);
    await load();
  };

  const handleRejectGuardrail = async (id: string, decisionData: GuardrailRequestDecisionIn) => {
    await api.rejectGuardrailRequest(id, decisionData);
    await load();
  };

  const handleSetTeamBudget = async (teamId: string, budgetUsd: number | null) => {
    const result = await api.setTeamBudget(teamId, { team_budget_usd: budgetUsd });
    await load();
    return result.warning;
  };

  const handleDownloadChargeback = async () => {
    setDownloadingCsv(true);
    try {
      const month = new Date().toISOString().slice(0, 7); // current month YYYY-MM
      await api.downloadChargebackCsv(month);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloadingCsv(false);
    }
  };

  useEffect(() => {
    void load();
    return () => {
      generation.current++;
    };
  }, [load]);

  return (
    <section className="admin-panel" aria-labelledby="admin-heading">
      <div className="section-head">
        <h2 id="admin-heading">Admin tools</h2>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'unmanaged' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('unmanaged')}
          disabled={loading}
        >
          Unmanaged keys
        </button>
        <button
          className={activeTab === 'budget' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('budget')}
          disabled={loading}
        >
          Budget requests
        </button>
        <button
          className={activeTab === 'guardrail' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('guardrail')}
          disabled={loading}
        >
          Guardrail requests
        </button>
        <button
          className={activeTab === 'drift' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('drift')}
          disabled={loading}
        >
          Drift
        </button>
        <button
          className={activeTab === 'jobs' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('jobs')}
          disabled={loading}
        >
          Job health
        </button>
        <button
          className={activeTab === 'teams' ? 'admin-tab admin-tab-active' : 'admin-tab'}
          onClick={() => setActiveTab('teams')}
          disabled={loading}
        >
          Teams
        </button>
        <button onClick={() => void load()} disabled={loading} className="admin-refresh" style={{ marginLeft: 'auto' }} title="Refresh admin data">
          {loading ? 'Loading...' : 'Reload'}
        </button>
      </div>

      {error && <div className="callout callout-warning">{error}</div>}

      {activeTab === 'unmanaged' &&
        (data.unmanaged ? (
          <UnmanagedKeyList
            items={data.unmanaged.items}
            teams={teams}
            onAfterClaim={async () => {
              await load();
              onClaimed?.();
            }}
          />
        ) : (
          <div className="empty"><p>Loading unmanaged keys...</p></div>
        ))}

      {activeTab === 'budget' &&
        (data.budgetRequests ? (
          <BudgetRequestList
            items={data.budgetRequests.items}
            identity={identity}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ) : (
          <div className="empty"><p>Loading budget requests...</p></div>
        ))}

      {activeTab === 'guardrail' &&
        (data.guardrailRequests ? (
          <GuardrailRequestList
            items={data.guardrailRequests.items}
            identity={identity}
            onApprove={handleApproveGuardrail}
            onReject={handleRejectGuardrail}
          />
        ) : (
          <div className="empty"><p>Loading guardrail requests...</p></div>
        ))}

      {activeTab === 'drift' &&
        (data.drift ? (
          <DriftList driftList={data.drift} />
        ) : (
          <div className="empty"><p>Loading drift data...</p></div>
        ))}

      {activeTab === 'jobs' &&
        (data.jobs ? (
          <JobStatusView jobs={data.jobs} />
        ) : (
          <div className="empty"><p>Loading job status...</p></div>
        ))}

      {activeTab === 'teams' &&
        (data.teamConfig ? (
          <>
            <div style={{ marginBottom: '12px' }}>
              <button onClick={handleDownloadChargeback} disabled={downloadingCsv}>
                {downloadingCsv ? 'Downloading...' : 'Download chargeback CSV'}
              </button>
              <span className="muted small" style={{ marginLeft: '8px' }}>
                (current month, admin/lead only)
              </span>
            </div>
            <TeamConfigList teams={data.teamConfig} onSetBudget={handleSetTeamBudget} />
          </>
        ) : (
          <div className="empty"><p>Loading team configuration...</p></div>
        ))}
    </section>
  );
}
